import React from 'react';
import {Box, Chip, MenuItem, Paper, Stack, TextField, Tooltip, Typography} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {FULLSCREEN_OVERLAY_SX, FullscreenToggle} from '../../shared/Fullscreen';
import {TrendWindow} from '../../shared/TrendWindowSelect';
import {BarrierHealthEntry, BarrierTrend, TimeBucketMeta} from '../../../utils/barrierAnalysisTypes';
import {BarrierTrendStat, BarrierWindowMtbf, GapDistBarrier, TopNSort} from './types';
import {
    LOW_CONFIDENCE_CHALLENGES,
    SILENT_KILLER_FEXP_THRESHOLD,
    TREND_STABLE_THRESHOLD,
    fexpPillStyle,
} from './barrierThresholds';

interface TopBarriersPanelProps {
    filteredTopCritical: BarrierHealthEntry[];
    extendedStatusCounts: {critical: number; warning: number; total: number};
    topN: number;
    setTopN: React.Dispatch<React.SetStateAction<number>>;
    topStatusFilter: 'all' | 'critical' | 'warning';
    setTopStatusFilter: React.Dispatch<React.SetStateAction<'all' | 'critical' | 'warning'>>;
    topSort: TopNSort;
    setTopSort: React.Dispatch<React.SetStateAction<TopNSort>>;
    topViewMode: 'window' | 'allTime';
    barrierTrendWindow: TrendWindow;
    /** All-time scored (challenged) reports — denominator for all-time Demand Coverage / F.Exp. */
    allTimeScoredReports: number;
    /** Recent-window scored (challenged) reports — denominator for window Demand Coverage / F.Exp. */
    windowScoredReports: number;
    barrierTrendStats: Map<number, BarrierTrendStat>;
    barrierTrends: Map<number, BarrierTrend>;
    barrierWindowMtbf: Map<number, BarrierWindowMtbf>;
    barrierMtbf: Map<number, number | null>;
    timeGranularity: 'monthly' | 'quarterly' | 'yearly';
    timeBuckets: TimeBucketMeta[];
    bandBucketCutoffKeys: {band: string; recent: string} | null;
    fullscreen: boolean;
    onToggleFullscreen: () => void;
    setGapDistBarrier: React.Dispatch<React.SetStateAction<GapDistBarrier | null>>;
    setDrillBarrier: React.Dispatch<React.SetStateAction<{nodeId: number; name: string} | null>>;
    setDrillPage: React.Dispatch<React.SetStateAction<number>>;
}

/** "Needs attention" panel: the Top N barriers requiring attention, with status
 *  filter, sort, fullscreen, and per-row recent-vs-prior trend cells. Pure
 *  presentation — all data is memoised in the parent (Barrier Effectiveness). */
export const TopBarriersPanel: React.FC<TopBarriersPanelProps> = ({
    filteredTopCritical,
    extendedStatusCounts,
    topN,
    setTopN,
    topStatusFilter,
    setTopStatusFilter,
    topSort,
    setTopSort,
    topViewMode,
    barrierTrendWindow,
    allTimeScoredReports,
    windowScoredReports,
    barrierTrendStats,
    barrierTrends,
    barrierWindowMtbf,
    barrierMtbf,
    timeGranularity,
    timeBuckets,
    bandBucketCutoffKeys,
    fullscreen,
    onToggleFullscreen,
    setGapDistBarrier,
    setDrillBarrier,
    setDrillPage,
}) => (
    <Paper
        variant="outlined"
        sx={{
            order: 3, mb: 1.5, px: 2, py: 1.5, display: 'flex', flexDirection: 'column', scrollMarginTop: '96px',
            ...(fullscreen && {
                ...FULLSCREEN_OVERLAY_SX,
                overflow: 'auto',
            }),
        }}
    >
        <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1}}>
                <Typography variant="subtitle2">Top</Typography>
                <TextField
                    select
                    size="small"
                    value={topN}
                    onChange={(e): void => setTopN(Number(e.target.value))}
                    sx={{minWidth: 60, '& .MuiInputBase-input': {fontSize: '0.75rem', py: 0.5}}}
                >
                    {[3, 5, 10, 15, 20].filter((n) => n <= filteredTopCritical.length).map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                    <MenuItem value={Infinity}>All ({filteredTopCritical.length})</MenuItem>
                </TextField>
                <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, flexGrow: 1}}>
                    <Typography variant="subtitle2">Barriers Requiring Attention</Typography>
                    {/* Methods legend — one-stop tooltip explaining what each trend signal in
                                        this table actually measures. Discovery point for newcomers;
                                        the per-column `?` icons hold the same info for power users. */}
                    <Tooltip
                        arrow
                        slotProps={{tooltip: {sx: {maxWidth: 480}}}}
                        title={
                            <Box sx={{fontSize: '0.7rem', lineHeight: 1.5}}>
                                <Typography variant="caption" sx={{fontWeight: 'bold', display: 'block', mb: 0.5}}>How the trend signals work</Typography>
                                <Box sx={{display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5}}>
                                    <strong>F.Exp</strong><span>Formula: Demand × (1 − Effectiveness Rate).</span>
                                    <strong>Status</strong><span>Threshold on the effectiveness rate (Critical &lt; 70 %, Warning 70–90 %, Healthy ≥ 90 %).</span>
                                    <strong>Trend</strong><span>Ordinary Least Squares slope of monthly effectiveness rates, pp/period. ↑/↓ from sign, "Flat" below noise.</span>
                                    <strong>Change</strong><span>Period delta (recent rate − prior rate) in pp. NOT a slope — direct difference of two averages.</span>
                                    <strong>MTBF</strong><span>Window mode: recent gap with a wear-out arrow comparing recent vs prior gaps by ratio (≥ 30 % shift).</span>
                                    <strong>Outlook</strong><span>Composite: blends period delta + within-recent slope + status. Tags Emerging / Escalating / Improving / Stable.</span>
                                </Box>
                                <Typography variant="caption" sx={{display: 'block', mt: 0.5, fontStyle: 'italic', color: 'rgba(255,255,255,0.7)'}}>
                                                    Hover any column header for the same explanation per-column.
                                </Typography>
                            </Box>
                        }
                    >
                        <HelpOutlineIcon sx={{fontSize: 15, color: 'text.disabled', cursor: 'help'}} />
                    </Tooltip>
                </Box>
                {topViewMode === 'window' && (
                    <Tooltip title="The Change column and Outlook signal use this trend window. Adjust it from the icon row at the top of the panel." arrow>
                        <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', cursor: 'help', mr: 1, whiteSpace: 'nowrap'}}>
                                            Trend window: <strong>{barrierTrendWindow === 'midpoint' ? 'Mid-point' : barrierTrendWindow === 'allTime' ? 'All time' : `${barrierTrendWindow} mo`}</strong>
                        </Typography>
                    </Tooltip>
                )}
                <TextField
                    select
                    size="small"
                    label="Sort"
                    value={topSort}
                    onChange={(e): void => setTopSort(e.target.value as TopNSort)}
                    sx={{minWidth: 160, mr: 1, '& .MuiInputBase-input': {fontSize: '0.75rem', py: 0.5}}}
                >
                    <MenuItem value="failureExposure">Failure exposure</MenuItem>
                    {topViewMode === 'window' && <MenuItem value="mostDegraded">Most degraded</MenuItem>}
                    {topViewMode === 'window' && <MenuItem value="mostImproved">Most improved</MenuItem>}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Status"
                    value={topStatusFilter}
                    onChange={(e): void => setTopStatusFilter(e.target.value as 'all' | 'critical' | 'warning')}
                    sx={{minWidth: 120, mr: 1, '& .MuiInputBase-input': {fontSize: '0.75rem', py: 0.5}}}
                >
                    <MenuItem value="all">All ({extendedStatusCounts.total})</MenuItem>
                    <MenuItem value="critical">Critical ({extendedStatusCounts.critical})</MenuItem>
                    <MenuItem value="warning">Warning ({extendedStatusCounts.warning})</MenuItem>
                </TextField>
                <FullscreenToggle active={fullscreen} onToggle={onToggleFullscreen} />
            </Stack>
            <table style={{borderCollapse: 'collapse', width: '100%', fontSize: '0.75rem'}}>
                <thead>
                    <tr>
                        {(['Barrier', 'Status', 'F.Exp', 'Success', 'Failures', 'Demand', 'Effectiveness Rate', 'Effectiveness Line', 'Trend', 'Change', 'MTBF', 'Outlook'] as const)
                            .filter((col) => {
                                // Window-only columns (period stats): hidden in all-time mode.
                                if (col === 'Change' || col === 'Outlook') return topViewMode === 'window';
                                // Effectiveness Line shows the full-history sparkline in all-time
                                // mode and a recent-window slice in window mode (see sparklineData
                                // below) — visible in both modes.
                                // MTBF is window-aware now (recent-window value with a wear-out
                                // arrow when compared to the prior window), so it stays visible
                                // in both modes.
                                return true;
                            })
                            .map((col) => (
                                <th
                                    key={col}
                                    style={{
                                        padding: '4px 6px',
                                        textAlign: col === 'Barrier' ? 'left' : 'center',
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase',
                                        fontSize: '0.6rem',
                                        letterSpacing: 0.5,
                                        color: '#666',
                                        borderBottom: '1px solid #e0e0e0',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {((): React.ReactNode => {
                                        // Per-column methodology tooltips. Each phrase names the
                                        // underlying statistical method so the analyst can read the
                                        // column without remembering which signals are ratios vs.
                                        // slopes vs. thresholds. Columns not in the map render as plain
                                        // text (counts and labels don't need methodology hints).
                                        const tip: Record<string, string> = {
                                            'Status': 'Health label derived from the Effectiveness Rate column: Critical when the rate is < 70 %, Warning when 70–90 %, Healthy when ≥ 90 %. In window mode the rate is the recent-window rate; an asterisk marks rows that fell back to the all-time rate because the recent window had no challenges.',
                                            'F.Exp': 'Failure Exposure = Demand Coverage × (1 − Effectiveness Rate). Measures how often a barrier is both challenged and fails. Tinted yellow at ≥ 5 % (moderate threshold) and red at ≥ 10 %.',
                                            'Demand': 'Demand Coverage = (Success + Failure) / Total Mapped Reports. How often the barrier is challenged.',
                                            'Effectiveness Rate': 'Success / (Success + Failure). The fraction of challenges where the barrier succeeded. In window mode this is the recent-window rate.',
                                            'Effectiveness Line': 'Sparkline of monthly effectiveness rates. All-time mode draws the full history; window mode draws only the recent-window months.',
                                            'Trend': 'Ordinary Least Squares slope of monthly effectiveness rates, in percentage points per period. ↑/↓ reflect the slope sign; "Flat" appears when the slope falls below the noise threshold. Methodology: per-month linear fit over the visible window.',
                                            'Change': 'Recent-window effectiveness rate minus prior-window rate, in percentage points. A period-over-period delta (not a slope) — direct difference of the two window averages.',
                                            'MTBF': 'Mean Time Between Failures (days). All-time mode shows the average gap across the full history. Window mode shows the recent-window gap with a wear-out arrow that compares recent vs. prior gaps via ratio (↓ red when recent gaps are ≥ 30 % shorter, ↑ green when ≥ 30 % longer).',
                                            'Outlook': 'Composite signal blending the period delta (Change column), the within-recent slope, and the status. Tags: Emerging (was healthy, now critical), Escalating (already critical and worsening), Improving (positive delta), Stable.',
                                        };
                                        const text = tip[col];
                                        if (!text) return col;
                                        return (
                                            <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.25}}>
                                                {col}
                                                <Tooltip title={text} arrow>
                                                    <HelpOutlineIcon sx={{fontSize: 11, color: 'text.disabled', cursor: 'help'}} />
                                                </Tooltip>
                                            </Box>
                                        );
                                    })()}
                                </th>
                            ))}
                    </tr>
                </thead>
                <tbody>
                    {filteredTopCritical.slice(0, topN).map((b) => {
                        const trend = barrierTrends.get(b.nodeId);
                        // Trend column slope:
                        //   - all-time mode: worker's `effectivenessSlope` fit per user-selected
                        //     granularity bucket (matches the main table's all-time E.Trd).
                        //   - window mode: `barrierTrendStats.recentSlope`, always monthly.
                        const periodStatsForTrend = barrierTrendStats.get(b.nodeId);
                        const slope = topViewMode === 'allTime'
                            ? (trend?.effectivenessSlope ?? null)
                            : (periodStatsForTrend?.recentSlope ?? null);
                        const slopeUnit = topViewMode === 'allTime'
                            ? (timeGranularity === 'monthly' ? 'mo' : timeGranularity === 'quarterly' ? 'q' : 'yr')
                            : 'mo';
                        const slopeAbs = slope != null ? Math.abs(slope) : null;
                        const isStable = slopeAbs != null && slopeAbs < TREND_STABLE_THRESHOLD;
                        const pctChange = slope != null ? Math.round(slope * 100) : null;
                        const trendLabel = slope == null ? '—'
                            : isStable ? 'Flat'
                                : `${slope > 0 ? '+' : ''}${pctChange}%/${slopeUnit}`;
                        const trendArrow = slope == null ? '' : isStable ? '→' : slope > 0 ? '↑' : '↓';
                        const trendColor = slope == null ? '#999' : isStable ? '#666' : slope > 0 ? '#2e7d32' : '#d32f2f';

                        // Period stats now come from the user-configurable trend window
                        // (mid-point OR last N months vs prior N months). The sparkline data
                        // is still used for the visualization, but no longer for the
                        // "was/now" period comparison or the Emerging signal.
                        // In window mode the line shows ONLY the recent-window buckets so the
                        // column reads as "trajectory inside the window" rather than full history.
                        // Slice matches `bandBucketCutoffKeys.recent` semantics used by the rest
                        // of the file, so the line lines up with the trend stats above.
                        const fullSparkline = trend?.sparkline ?? [];
                        const sparklineData = ((): number[] => {
                            if (topViewMode !== 'window' || !bandBucketCutoffKeys) return fullSparkline;
                            const startIdx = timeBuckets.findIndex((bk) => bk.key >= bandBucketCutoffKeys.recent);
                            return startIdx >= 0 ? fullSparkline.slice(startIdx) : [];
                        })();
                        const validPts = sparklineData.filter((v) => v >= 0);
                        const periodStats = barrierTrendStats.get(b.nodeId);
                        const priorEffRate = periodStats?.priorEffRate ?? null;
                        const recentEffRate = periodStats?.recentEffRate ?? null;
                        const deltaEffRate = periodStats?.deltaEffRate ?? null;
                        // `deltaPp` is the integer pp delta the Change column displays.
                        // Using it as the signal driver makes the Outlook chip match what
                        // analysts see in the Change column.
                        const deltaPp = deltaEffRate != null ? Math.round(deltaEffRate * 100) : null;
                        // recentSlope (pp/month) is the within-recent regression slope. Used
                        // as a "still declining" confirmation in the Emerging fallback.
                        const recentSlopePp = periodStats?.recentSlope != null
                            ? Math.round(periodStats.recentSlope * 100)
                            : null;
                        // Change column values are hoisted here so the Outlook signal text
                        // can quote them in its "was X%, now Y%" suffix.
                        const prevPct = priorEffRate != null ? Math.round(priorEffRate * 100) : null;
                        const currPct = recentEffRate != null ? Math.round(recentEffRate * 100) : null;
                        const changePct = deltaEffRate != null ? Math.round(deltaEffRate * 100) : null;
                        // View-mode-aware cell values: in window mode, derive Success/Failures/
                        // Demand/Effectiveness Rate from the recent window; in all-time mode use
                        // the cumulative figures from the worker.
                        const inWindowMode = topViewMode === 'window';
                        const cellSuccess = inWindowMode ? (periodStats?.recentSuccess ?? 0) : b.successCount;
                        const cellFailure = inWindowMode ? (periodStats?.recentFailure ?? 0) : b.failureCount;
                        const cellChallenged = cellSuccess + cellFailure;
                        const cellTotalReports = inWindowMode ? windowScoredReports : allTimeScoredReports;
                        const cellDemandCoverage = cellTotalReports > 0 ? cellChallenged / cellTotalReports : 0;
                        const cellEffRate = inWindowMode
                            ? (recentEffRate ?? 0)
                            : b.effectivenessRate;
                        const cellEffRateNull = inWindowMode && recentEffRate == null;
                        const ratePct = Math.round(cellEffRate * 100);
                        // Emerging — any of:
                        //   (1) was healthy in prior, now critical (< 70%)
                        //   (2) newly active in recent window (no prior data) AND currently critical
                        //   (3) warning status with a sharp recent decline (slope <= -5%/mo)
                        const recentChallenges = periodStats?.recentChallenges ?? 0;
                        const priorChallenges = periodStats?.priorChallenges ?? 0;
                        // Soft low-confidence cue: rows that survived the hard `topMinSample`
                        // filter but still have very few recent challenges get dimmed.
                        const lowConfidence = inWindowMode && recentChallenges >= 1 && recentChallenges < LOW_CONFIDENCE_CHALLENGES;
                        const lowConfTip = `Only ${recentChallenges} challenge${recentChallenges === 1 ? '' : 's'} in the recent window — thin sample, treat the trend slope with caution.`;
                        const newlyActiveCritical =
                                            priorChallenges === 0
                                            && recentChallenges >= 1
                                            && recentEffRate != null
                                            && recentEffRate < 0.7;
                        const wasHealthyNowCritical =
                                            priorEffRate != null && priorEffRate >= 0.7
                                            && recentEffRate != null && recentEffRate < 0.7;
                        const warningSharpDecline =
                                            b.status === 'warning'
                                            && recentSlopePp != null && recentSlopePp <= -5;
                        const isEmerging = wasHealthyNowCritical || newlyActiveCritical || warningSharpDecline;

                        // Outlook signal — driven by the period delta (deltaPp) so it tracks
                        // the same number the Change column shows. Stable threshold = anything
                        // that rounds to 0pp avoids flapping on tiny noise.
                        let signal: string;
                        let signalBg: string;
                        let signalColor: string;
                        let signalReason: string;
                        const wasNowSuffix = (prevPct != null && currPct != null)
                            ? ` (was ${prevPct}%, now ${currPct}%)` : '';
                        if (isEmerging) {
                            signal = 'Emerging'; signalBg = 'rgba(156, 39, 176, 0.12)'; signalColor = '#7b1fa2';
                            signalReason = wasHealthyNowCritical
                                ? `Was healthy in the prior period and dropped below the 70% threshold${wasNowSuffix}.`
                                : newlyActiveCritical
                                    ? `Newly active in the recent window (${recentChallenges} challenge${recentChallenges !== 1 ? 's' : ''}, no prior history) and currently below 70%${currPct != null ? ` (now ${currPct}%)` : ''}.`
                                    : `Warning status with effectiveness declining within the recent window (slope ${recentSlopePp ?? 0}%/mo).`;
                        } else if (b.status === 'critical' && deltaPp != null && deltaPp < 0) {
                            signal = 'Escalating'; signalBg = 'rgba(211, 47, 47, 0.12)'; signalColor = '#d32f2f';
                            signalReason = `Critical status and effectiveness dropped ${deltaPp}pp vs. the prior period${wasNowSuffix}.`;
                        } else if (b.status === 'critical' || (b.status === 'warning' && deltaPp != null && deltaPp < 0)) {
                            signal = 'Caution'; signalBg = 'rgba(255, 152, 0, 0.12)'; signalColor = '#f57c00';
                            if (b.status === 'critical') {
                                // Critical, but no period drop (otherwise it would have been
                                // Escalating). Explain why Caution sticks.
                                signalReason = deltaPp == null
                                    ? `Critical effectiveness rate (${ratePct}%). Not enough prior-period data to compute a recent change.`
                                    : `Critical effectiveness rate (${ratePct}%) with no recent degradation (Δ ${deltaPp >= 0 ? '+' : ''}${deltaPp}pp${wasNowSuffix}). Stays in Caution until effectiveness rises above critical.`;
                            } else {
                                signalReason = `Warning status with effectiveness dropping ${deltaPp}pp vs. the prior period${wasNowSuffix}.`;
                            }
                        } else if (deltaPp != null && deltaPp > 0) {
                            signal = 'Improving'; signalBg = 'rgba(46, 125, 50, 0.12)'; signalColor = '#2e7d32';
                            signalReason = `Effectiveness rose +${deltaPp}pp vs. the prior period${wasNowSuffix}.`;
                        } else {
                            signal = 'Stable'; signalBg = 'rgba(158, 158, 158, 0.12)'; signalColor = '#757575';
                            signalReason = deltaPp == null
                                ? 'Not enough data in the prior period to compute a period-over-period change.'
                                : `No meaningful change vs. the prior period (Δ ${deltaPp >= 0 ? '+' : ''}${deltaPp}pp${wasNowSuffix}).`;
                        }

                        const barColor = b.status === 'critical' ? '#d32f2f'
                            : b.status === 'warning' ? '#f57c00'
                                : '#2e7d32';

                        // Silent-killer detection: effective status is Healthy
                        // (i.e. Effectiveness Rate ≥ 90%) yet Failure Exposure
                        // is high enough to matter at the system level. Surfaces
                        // the high-effectiveness × high-demand pattern that
                        // Effectiveness-only signals would let slip past.
                        const effectiveStatusForRow: 'critical' | 'warning' | 'healthy' =
                                            periodStats?.windowAwareStatus ?? b.status;
                        const effectiveFExp = inWindowMode
                            ? (windowScoredReports > 0 ? (periodStats?.recentFailure ?? 0) / windowScoredReports : 0)
                            : b.failureExposure;
                        const isSilentKiller =
                                            effectiveStatusForRow === 'healthy'
                                            && effectiveFExp >= SILENT_KILLER_FEXP_THRESHOLD;

                        // Build sparkline SVG
                        let sparklineSvg: React.ReactElement | null = null;
                        if (validPts.length >= 2) {
                            const w = 100;
                            const h = 20;
                            const pad = 2;
                            const min = 0;
                            const range = 1;
                            const validIndices = sparklineData.map((v, i) => v >= 0 ? i : -1).filter((i) => i >= 0);
                            const xScale = validIndices.length > 1 ? (w - 2 * pad) / (validIndices.length - 1) : 0;
                            const points = validIndices.map((_, j) => {
                                const x = pad + j * xScale;
                                const y = pad + (1 - (validPts[j] - min) / range) * (h - 2 * pad);
                                return `${x},${y}`;
                            }).join(' ');
                            sparklineSvg = (
                                <svg width={w} height={h}>
                                    <polyline points={points} fill="none" stroke={barColor} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
                                </svg>
                            );
                        }

                        const tdStyle: React.CSSProperties = {padding: '4px 6px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle'};

                        return (
                            <tr key={b.name}>
                                <td style={{...tdStyle, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500, fontSize: '0.8rem'}} title={`${b.fullName} — click to view its reports`}>
                                    <span
                                        onClick={(): void => { setDrillBarrier({nodeId: b.nodeId, name: b.fullName}); setDrillPage(0); }}
                                        style={{color: '#1976d2', cursor: 'pointer', textDecoration: 'underline'}}
                                    >
                                        {b.name}
                                    </span>
                                </td>
                                <td style={{...tdStyle, textAlign: 'center'}}>
                                    <Stack spacing={0.4} alignItems="center">
                                        {((): React.ReactElement => {
                                            // Window-aware status when the recent window has data; otherwise
                                            // fall back to the worker's cumulative status with a `*` marker.
                                            const usingFallback = periodStats?.windowAwareStatus == null;
                                            const tipMain = effectiveStatusForRow === 'critical' ? 'Recent-window effectiveness below 70%'
                                                : effectiveStatusForRow === 'warning' ? 'Recent-window effectiveness between 70% and 90%'
                                                    : 'Recent-window effectiveness 90% or above';
                                            const tip = usingFallback
                                                ? 'Status computed from all-time history — no challenges in the recent window.'
                                                : `${tipMain} (recent: ${currPct ?? '—'}%, ${periodStats?.recentChallenges ?? 0} challenges).`;
                                            const color = effectiveStatusForRow === 'critical' ? '#d32f2f'
                                                : effectiveStatusForRow === 'warning' ? '#f57c00'
                                                    : '#2e7d32';
                                            const bg = effectiveStatusForRow === 'critical' ? 'rgba(211, 47, 47, 0.12)'
                                                : effectiveStatusForRow === 'warning' ? 'rgba(255, 152, 0, 0.12)'
                                                    : 'rgba(46, 125, 50, 0.12)';
                                            const label = effectiveStatusForRow === 'critical' ? 'Critical' : effectiveStatusForRow === 'warning' ? 'Warning' : 'Healthy';
                                            return (
                                                <Tooltip title={tip} arrow>
                                                    <Chip
                                                        size="small"
                                                        label={`${label}${usingFallback ? ' *' : ''}`}
                                                        sx={{
                                                            height: 20, fontSize: '0.6rem', fontWeight: 'bold',
                                                            backgroundColor: bg,
                                                            color,
                                                            opacity: usingFallback ? 0.7 : 1,
                                                        }}
                                                    />
                                                </Tooltip>
                                            );
                                        })()}
                                        {isSilentKiller && (
                                            <Tooltip
                                                title={`Silent killer: Effectiveness Rate ≥ 90% (looks "Healthy") but Failure Exposure is ${(effectiveFExp * 100).toFixed(1)}% — challenged so often that the absolute failure count is significant. Promoted into this panel by the silent-killer rule (F.Exp ≥ ${Math.round(SILENT_KILLER_FEXP_THRESHOLD * 100)}%).`}
                                                arrow
                                            >
                                                <Chip
                                                    size="small"
                                                    icon={<WarningAmberIcon sx={{fontSize: 12, color: '#ff6f00 !important'}} />}
                                                    label="Silent killer"
                                                    sx={{
                                                        height: 18, fontSize: '0.55rem', fontWeight: 'bold',
                                                        backgroundColor: 'rgba(255, 111, 0, 0.15)',
                                                        color: '#ff6f00',
                                                        border: '1px solid rgba(255, 111, 0, 0.4)',
                                                        '& .MuiChip-icon': {ml: 0.25, mr: -0.25},
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </td>
                                {/* F.Exp — placed right after Status so the colored Status chip
                                                     and the colored F.Exp magnitude read together. No thin-sample
                                                     fade: F.Exp's denominator is fixed window-wide reports, so it's
                                                     not a small-sample estimator. */}
                                <td style={{...tdStyle, textAlign: 'center', fontSize: '0.8rem'}}>
                                    {((): React.ReactNode => {
                                        const pct = Math.round(effectiveFExp * 100);
                                        const pill = fexpPillStyle(pct);
                                        return <span style={pill ?? {color: '#666'}}>{pct}%</span>;
                                    })()}
                                </td>
                                <td style={{...tdStyle, textAlign: 'center', fontWeight: 'bold', color: '#2e7d32', fontSize: '0.8rem'}}>
                                    {cellSuccess}
                                </td>
                                <td style={{...tdStyle, textAlign: 'center', fontWeight: 'bold', color: '#d32f2f', fontSize: '0.8rem'}}>
                                    {cellFailure}
                                </td>
                                <td style={{...tdStyle, textAlign: 'center', fontSize: '0.8rem', color: '#666', cursor: 'help'}} title={`Challenged in ${Math.round(cellDemandCoverage * 100)}% of ${inWindowMode ? 'reports in the recent window' : 'reports'}`}>
                                    {Math.round(cellDemandCoverage * 100)}%
                                </td>
                                <td style={{...tdStyle, minWidth: 100}}>
                                    {cellEffRateNull ? (
                                        <Typography variant="body2" sx={{fontSize: '0.75rem', color: 'text.disabled', textAlign: 'center'}}>—</Typography>
                                    ) : (
                                        <div style={{position: 'relative', height: 18, backgroundColor: '#f0f0f0', borderRadius: 4, overflow: 'hidden'}}>
                                            <div style={{width: `${ratePct}%`, height: '100%', backgroundColor: barColor, borderRadius: 4, transition: 'width 0.3s'}} />
                                            <span style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 'bold', color: ratePct >= 45 ? '#fff' : '#333'}}>
                                                {ratePct}%
                                            </span>
                                        </div>
                                    )}
                                </td>
                                <td style={{...tdStyle, textAlign: 'center', minWidth: 80}}>
                                    {sparklineSvg}
                                </td>
                                <td style={{...tdStyle, textAlign: 'center', fontSize: '0.75rem', color: trendColor, whiteSpace: 'nowrap', opacity: lowConfidence ? 0.55 : 1}}>
                                    {((): React.ReactNode => {
                                        if (slope == null) return <span>—</span>;
                                        const scope = topViewMode === 'allTime'
                                            ? `Effectiveness slope across the full history (${timeGranularity} buckets).`
                                            : 'Effectiveness slope within the recent window (monthly buckets).';
                                        const direction = isStable
                                            ? `Stable (${slope > 0 ? '+' : ''}${pctChange}%/${slopeUnit}).`
                                            : `${slope > 0 ? 'Improving' : 'Degrading'}: ${slope > 0 ? '+' : ''}${pctChange}%/${slopeUnit}.`;
                                        const lowConfSuffix = lowConfidence ? ` ${lowConfTip}` : '';
                                        return (
                                            <Tooltip title={`${direction} ${scope}${lowConfSuffix}`} arrow>
                                                <span style={{cursor: 'help'}}>{trendArrow} {trendLabel}</span>
                                            </Tooltip>
                                        );
                                    })()}
                                </td>
                                {inWindowMode && (
                                    <td style={{...tdStyle, textAlign: 'center', whiteSpace: 'nowrap'}}>
                                        {changePct != null ? (
                                            <Tooltip title={`Was ${prevPct}%, now ${currPct}%`} arrow>
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '1px 6px',
                                                    borderRadius: 8,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 'bold',
                                                    backgroundColor: changePct < 0 ? 'rgba(211, 47, 47, 0.1)' : changePct > 0 ? 'rgba(46, 125, 50, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                                                    color: changePct < 0 ? '#d32f2f' : changePct > 0 ? '#2e7d32' : '#757575',
                                                }}>
                                                    {changePct > 0 ? '+' : ''}{changePct}%
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <span style={{color: '#999', fontSize: '0.7rem'}}>—</span>
                                        )}
                                    </td>
                                )}
                                {/* MTBF — cumulative in all-time mode, recent-window with wear-out arrow in window mode.
                                                     The numeric value is clickable: opens the failure-gap distribution dialog
                                                     so analysts can see whether the mean is representative or hides clustering. */}
                                {((): React.ReactElement => {
                                    const openDist = (): void => setGapDistBarrier({nodeId: b.nodeId, name: b.fullName, uniqueId: b.name});
                                    const linkStyle: React.CSSProperties = {color: '#1976d2', cursor: 'pointer', textDecoration: 'none'};
                                    const onEnter = (e: React.MouseEvent<HTMLSpanElement>): void => { e.currentTarget.style.textDecoration = 'underline'; };
                                    const onLeave = (e: React.MouseEvent<HTMLSpanElement>): void => { e.currentTarget.style.textDecoration = 'none'; };
                                    if (!inWindowMode) {
                                        const m = barrierMtbf.get(b.nodeId);
                                        return (
                                            <td style={{...tdStyle, textAlign: 'center', fontSize: '0.8rem', color: '#666'}} title="Mean Time Between Failures — average days between consecutive failure events. Click for full distribution.">
                                                {m != null
                                                    ? <span onClick={openDist} onMouseEnter={onEnter} onMouseLeave={onLeave} style={linkStyle}>{`${m}d`}</span>
                                                    : '—'}
                                            </td>
                                        );
                                    }
                                    const wm = barrierWindowMtbf.get(b.nodeId);
                                    const recent = wm?.recent ?? null;
                                    const prior = wm?.prior ?? null;
                                    if (recent == null) {
                                        const tip = prior != null
                                            ? `No recent-window MTBF (fewer than 2 failures in the recent half). Prior: ${prior}d.`
                                            : 'Need at least 2 failures inside a window to compute MTBF.';
                                        return (
                                            <td style={{...tdStyle, textAlign: 'center', fontSize: '0.8rem', color: '#bdbdbd', cursor: 'help'}} title={tip}>—</td>
                                        );
                                    }
                                    let arrow = '';
                                    let mtbfColor: string = '#666';
                                    let signalLine = '';
                                    if (prior != null && prior > 0) {
                                        const ratio = recent / prior;
                                        if (ratio < 0.7) { arrow = '↓'; mtbfColor = '#d32f2f'; signalLine = `Wear-out signal: recent gaps are ~${(prior / recent).toFixed(1)}× shorter than prior.`; }
                                        else if (ratio > 1.3) { arrow = '↑'; mtbfColor = '#2e7d32'; signalLine = `Improving: recent gaps are ~${(recent / prior).toFixed(1)}× longer than prior.`; }
                                        else { arrow = '→'; mtbfColor = '#999'; signalLine = 'Stable: recent and prior MTBF are within ±30%.'; }
                                    }
                                    const tip = prior != null
                                        ? `Recent: ${recent}d, prior: ${prior}d. ${signalLine} Click for full distribution.`
                                        : `Recent: ${recent}d. Not enough prior-window failures to compare. Click for full distribution.`;
                                    const linkStyleWindow: React.CSSProperties = {color: mtbfColor, cursor: 'pointer', textDecoration: 'none', fontWeight: arrow === '↓' || arrow === '↑' ? 'bold' : 'normal'};
                                    return (
                                        <td style={{...tdStyle, textAlign: 'center', fontSize: '0.8rem', color: mtbfColor, fontWeight: arrow === '↓' || arrow === '↑' ? 'bold' : 'normal'}} title={tip}>
                                            <span onClick={openDist} onMouseEnter={onEnter} onMouseLeave={onLeave} style={linkStyleWindow}>
                                                {recent}d{arrow ? ` ${arrow}` : ''}
                                            </span>
                                        </td>
                                    );
                                })()}
                                {inWindowMode && (
                                    <td style={{...tdStyle, textAlign: 'center'}}>
                                        <Tooltip title={signalReason} arrow>
                                            <Chip
                                                size="small"
                                                label={signal}
                                                sx={{
                                                    height: 22, fontSize: '0.65rem', fontWeight: 'bold',
                                                    backgroundColor: signalBg,
                                                    color: signalColor,
                                                    cursor: 'help',
                                                }}
                                            />
                                        </Tooltip>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <Stack direction="row" spacing={2} sx={{mt: 1.5}} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography variant="caption" sx={{fontSize: '0.6rem', fontWeight: 'bold', color: '#666', textTransform: 'uppercase', letterSpacing: 0.5}}>Status:</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{width: 10, height: 10, borderRadius: 1, backgroundColor: 'rgba(211, 47, 47, 0.12)', border: '1px solid #d32f2f'}} />
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.6rem'}}>Critical — effectiveness below 70%</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{width: 10, height: 10, borderRadius: 1, backgroundColor: 'rgba(255, 152, 0, 0.12)', border: '1px solid #f57c00'}} />
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.6rem'}}>Warning — effectiveness between 70% and 90%</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{width: 10, height: 10, borderRadius: 1, backgroundColor: 'rgba(46, 125, 50, 0.12)', border: '1px solid #2e7d32'}} />
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.6rem'}}>Healthy — effectiveness 90% or above</Typography>
                </Stack>
            </Stack>
            <Stack direction="row" spacing={2} sx={{mt: 0.5}} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography variant="caption" sx={{fontSize: '0.6rem', fontWeight: 'bold', color: '#666', textTransform: 'uppercase', letterSpacing: 0.5}}>Outlook:</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{width: 10, height: 10, borderRadius: 1, backgroundColor: '#7b1fa2'}} />
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.6rem'}}>Emerging — was healthy, now declining toward critical</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{width: 10, height: 10, borderRadius: 1, backgroundColor: '#d32f2f'}} />
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.6rem'}}>Escalating — already critical and still worsening</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{width: 10, height: 10, borderRadius: 1, backgroundColor: '#f57c00'}} />
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.6rem'}}>Caution — critical but stable, or warning and worsening</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{width: 10, height: 10, borderRadius: 1, backgroundColor: '#2e7d32'}} />
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.6rem'}}>Improving — trend is positive</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{width: 10, height: 10, borderRadius: 1, backgroundColor: '#9e9e9e'}} />
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.6rem'}}>Stable — no significant change</Typography>
                </Stack>
            </Stack>
        </Box>
    </Paper>
);
